/**
 * Publish / take offline from the Journey editor (CC 0529).
 *
 * One status write, `POST /v1/admin/journeys/{id}/status` (setJourneyStatus), awaited. The journey the
 * screen keeps afterwards carries the SERVER's answer: its status, updatedAt and (when it sends one)
 * publishedAt and publicUrl, never a client-made "Just now".
 *
 * What publishing does NOT do from this editor: save its own edits. Publishing is a status transition, and
 * it serves whatever was last SAVED (api-client setAdminJourneyStatus). This editor's edits have no
 * contract-shaped save. Its journey model uses step names the admin patch refuses, so they stay on screen
 * (JourneysHome). With unsaved edits, the dialog says so and the success message says so. Nothing claims
 * those edits went live.
 */
import { journeyURL, journeyAddress, type Journey } from "./journey-model";

export type JourneyStatus = "draft" | "published";
export type StatusWrite = (id: string, status: JourneyStatus) => Promise<unknown>;
export interface StatusToast { tone: "success" | "danger" | "warn"; title: string; sub: string }

const str = (v: unknown): string => (typeof v === "string" ? v : "");

/** Write the status and answer the journey as the server now holds it. Throws on refusal, or when the
 *  answer's status is not the one asked for. */
export async function changeJourneyStatus(journey: Journey, status: JourneyStatus, write: StatusWrite): Promise<Journey> {
  const res = (await write(String(journey.id), status)) as Record<string, unknown> | null;
  const got = res && typeof res === "object" ? res : {};
  if (got.status !== status) throw new Error(`The server answered "${str(got.status) || "no status"}", not "${status}". Nothing changed here.`);
  const updatedAt = str(got.updatedAt) || null;
  const publishedAt = str(got.publishedAt) || (status === "published" ? updatedAt : null);
  return { ...journey, status, updatedAt, publishedAt, ...(str(got.publicUrl) ? { publicUrl: str(got.publicUrl) } : {}) };
}

/** The editor's whole Publish / Take offline step: the write, then the message to show. `ok` false means
 *  nothing changed; the caller keeps the dialog open and keeps its unsaved edits. */
export async function runEditorStatusChange(o: { journey: Journey; status: JourneyStatus; dirty: boolean; setStatus: (s: JourneyStatus) => Promise<Journey | void> }): Promise<{ ok: boolean; toast: StatusToast }> {
  const publishing = o.status === "published";
  try {
    const next = await o.setStatus(o.status);
    const url = (next && str((next as Journey).publicUrl)) || journeyURL(o.journey);
    if (publishing) {
      return { ok: true, toast: o.dirty
        ? { tone: "warn", title: "Journey published, without your edits", sub: url + " is live with its last saved settings. Changes made in this editor are not saved; save page settings from All Journeys." }
        : { tone: "success", title: "Journey published", sub: url + " is live" } };
    }
    return { ok: true, toast: { tone: "success", title: "Journey taken offline", sub: journeyAddress(o.journey) + " · link paused" } };
  } catch (err) {
    const msg = str((err as { message?: unknown } | null)?.message) || "The status change was refused. Nothing changed.";
    return { ok: false, toast: { tone: "danger", title: publishing ? "Journey not published" : "Journey still live", sub: msg } };
  }
}
